/*
 * The shared "find an entity" mini-modal (audit Phase 3.3). Replaces
 * wwwroot/js/EntityPicker/*.js on the Phase 3.1 core. No jQuery.
 *
 * One picker control is a wrapper carrying the pair of inputs it writes to:
 *
 *   <div class="entity-picker-control input-group input-group-sm">
 *       <input type="hidden" class="lc-add-person-id" />
 *       <input type="text" class="form-control entity-picker-display" readonly />
 *       <button data-action="Picker.Open"
 *               data-args='["person", { "domainIds": [1, 2] }]'>...</button>
 *   </div>
 *
 * `this` is the button, so Open finds its own wrapper and stashes the pair;
 * picking a row writes the id to the hidden input and the label to the display
 * one, then fires `change` on the hidden input -- which is the hook callers use
 * to auto-commit a pick (the License Domain Binder does).
 *
 * Two flavours, distinguished only by markup. ENTITY pickers (person, domain,
 * application, resource, persongroup, tenant) mean the id, and their display
 * input is readonly so the pair cannot drift apart. VOCABULARY pickers
 * (claimtype, claimvaluetype) mean the STRING, which arrives as the label, so
 * the caller leaves its input editable and reads it directly -- the picker is a
 * suggestion there, not a restriction.
 *
 * Registered under `Picker` (Phase 3.4). It spent 3.3 on the compat facade as
 * Mcp.Admin.EntityPicker.* because Feature, PersonGroup, SamlPeer and Tenant
 * were all still legacy and all still called it; the name moved with the last
 * of them.
 */
import { one } from '../core/dom';
import { request, xhrFromError } from '../core/http';
import { showModalElement, hideModalElement } from '../core/modal';

const MODAL_ID = 'EntityPickerModal';
const SEARCH_DEBOUNCE_MS = 250;

const TITLES: Record<string, string> = {
    person: 'Find Person',
    domain: 'Find Domain',
    application: 'Find Application',
    resource: 'Find Resource',
    persongroup: 'Find Person Group',
    tenant: 'Find Tenant',
    claimtype: 'Find Claim Type',
    claimvaluetype: 'Find Claim Value Type',
};

/** What the open picker is pointed at. Cleared on close so a stale modal cannot write. */
interface ActiveTarget {
    hidden: HTMLInputElement;
    display: HTMLInputElement | null;
    entityType: string;
    context: Record<string, unknown>;
}

let active: ActiveTarget | null = null;
let searchTimer: ReturnType<typeof setTimeout> | null = null;
let bound = false;

function modal(): HTMLElement | null {
    return one(`#${MODAL_ID}`) as HTMLElement | null;
}

/**
 * Query string for one search.
 *
 * Repeated keys, hand-built, rather than core/http's encodeParams: that helper
 * spells an array `domainIds[]=1&domainIds[]=2`, the PHP-style empty-bracket
 * form, and ASP.NET Core's model binder does not accept it -- a multi-domain
 * context would bind as an empty list and the picker would come back with no
 * rows at all. Repeated `domainIds=1&domainIds=2` is what binds, and it is what
 * the jQuery original got from `traditional: true`.
 */
export function pickerQuery(entityType: string, query: string, context: Record<string, unknown>): URLSearchParams {
    const params = new URLSearchParams();
    params.append('type', entityType);
    params.append('q', query);
    for (const [key, value] of Object.entries(context)) {
        if (value === null || value === undefined) continue;
        if (Array.isArray(value)) {
            for (const item of value) params.append(key, String(item));
        } else {
            params.append(key, String(value));
        }
    }
    return params;
}

async function runSearch(query: string): Promise<void> {
    const host = modal();
    if (!host || !active) return;
    const busy = host.querySelector('.entity-picker-busy');
    const results = host.querySelector('.entity-picker-results');
    if (!results) return;
    busy?.classList.remove('d-none');
    try {
        const result = await request(`EntityPicker?${pickerQuery(active.entityType, query, active.context)}`, { accept: 'html' });
        results.innerHTML = result.text;
    } catch (error) {
        const status = xhrFromError(error).status;
        results.textContent = '';
        const failed = document.createElement('div');
        failed.className = 'text-danger small p-2';
        failed.textContent = `Failed to load results (${status}).`;
        results.appendChild(failed);
    } finally {
        busy?.classList.add('d-none');
    }
}

/**
 * Open the picker against the calling button's own wrapper. `this` is the
 * button; data-action supplies the entity type and an optional context of
 * filters ({ domainIds }, { appId }) that narrow the result set.
 */
export function open(this: Element, entityType?: unknown, context?: unknown): void {
    const control = this.closest('.entity-picker-control');
    if (!control) {
        console.warn('EntityPicker.Open: no .entity-picker-control ancestor');
        return;
    }
    const hidden = control.querySelector('input[type=hidden]') as HTMLInputElement | null;
    if (!hidden) {
        console.warn('EntityPicker.Open: the control has no hidden input to write to');
        return;
    }
    const host = modal();
    if (!host) {
        // _EntityPickerModal is rendered by the layout, so this only happens
        // when a picker is used on a page that did not get it -- say so rather
        // than failing silently the way a bare showModal would.
        console.warn('EntityPicker.Open: #EntityPickerModal is not in the page');
        return;
    }

    active = {
        hidden,
        display: control.querySelector('.entity-picker-display') as HTMLInputElement | null,
        entityType: String(entityType ?? '').toLowerCase(),
        context: (context ?? {}) as Record<string, unknown>,
    };

    const title = host.querySelector('#EntityPickerModalTitle');
    if (title) title.textContent = TITLES[active.entityType] ?? 'Find';
    const search = host.querySelector('.entity-picker-search') as HTMLInputElement | null;
    if (search) search.value = '';
    const results = host.querySelector('.entity-picker-results');
    if (results) results.innerHTML = '<div class="text-secondary small p-2">Loading...</div>';

    host.addEventListener('shown.bs.modal', () => search?.focus(), { once: true });
    showModalElement(host);
    void runSearch('');
}

function onInput(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches(`#${MODAL_ID} .entity-picker-search`)) return;
    const query = input.value;
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => void runSearch(query), SEARCH_DEBOUNCE_MS);
}

function onClick(event: Event): void {
    const origin = event.target;
    if (!(origin instanceof Element)) return;
    const row = origin.closest(`#${MODAL_ID} .entity-picker-row`);
    if (!row || !active) return;
    const id = row.getAttribute('data-id') ?? '';
    const label = row.getAttribute('data-label');
    active.hidden.value = id;
    if (active.display) active.display.value = label || `#${id}`;
    // The pick, announced. A caller that commits on selection (the License
    // Domain Binder) listens for this; nothing breaks for one that does not.
    active.hidden.dispatchEvent(new Event('change', { bubbles: true }));
    const host = modal();
    if (host) hideModalElement(host);
}

function onHidden(event: Event): void {
    if (event.target instanceof Element && event.target.id === MODAL_ID) active = null;
}

/** Install the delegated listeners once; safe to call repeatedly. */
export function initEntityPicker(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('input', onInput);
    document.addEventListener('click', onClick);
    document.addEventListener('hidden.bs.modal', onHidden);
}

/** Test support. */
export function resetEntityPicker(): void {
    document.removeEventListener('input', onInput);
    document.removeEventListener('click', onClick);
    document.removeEventListener('hidden.bs.modal', onHidden);
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = null;
    active = null;
    bound = false;
}

/** The `Picker` area shape the views name: data-action="Picker.Open". */
export const EntityPicker = { Open: open };
